import { useEffect, useRef, useState } from "react";
import { createChart, ColorType, CandlestickSeries } from "lightweight-charts";
import type { IChartApi, ISeriesApi } from "lightweight-charts";
import { fetchPumpCandlesPaged } from "@/lib/pumpCandles";

export function MiniCandleChart({
  mint,
  height = 96,
  interval = "1s",
  rightOffset = 50,
  showLogo = true
}: {
  mint: string,
  height?: number,
  interval?: any,
  rightOffset?: number,
  showLogo?: boolean
}) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth || 300,
      height,
      layout: {
        background: { type: ColorType.Solid, color: "rgba(0, 0, 0, 0)" },
        textColor: "rgba(255, 255, 255, 0.4)",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(0, 0, 0, 0)" },
        horzLines: { color: "rgba(0, 0, 0, 0)" },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.2, bottom: 0.2 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: rightOffset + 20,
        barSpacing: 1.5,
        maxBarSpacing: 4,
        minBarSpacing: 0.5,
        shiftVisibleRangeOnNewBar: true,
      },
      crosshair: {
        vertLine: { labelVisible: false },
        horzLine: { labelVisible: false },
      },
      handleScroll: false,
      handleScale: false,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#14F195",
      downColor: "#FF4557",
      borderVisible: false,
      wickUpColor: "#14F195",
      wickDownColor: "#FF4557",
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [height, rightOffset]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(chartContainerRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (!mint || !isVisible) return;
    let mounted = true;
    setLoading(true);

    fetchPumpCandlesPaged(mint, { interval, pages: 1, skipSupplyFetch: true })
      .then(res => {
        if (!mounted || !seriesRef.current || !chartRef.current) return;
        if (res.candles && res.candles.length > 0) {
          seriesRef.current.setData(res.candles);
          requestAnimationFrame(() => {
            if (chartRef.current) {
              chartRef.current.timeScale().fitContent();
              chartRef.current.applyOptions({
                timeScale: {
                  rightOffset: 0,
                },
              });
            }
          });
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("MiniCandleChart fetch error:", err);
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [mint, interval, isVisible]);

  return (
    <div className="relative w-full h-full bg-black/20 overflow-hidden">
      <div ref={chartContainerRef} className="w-full" style={{ height }} />
      {loading && showLogo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity">
           <div className="flex flex-col items-center gap-2">
              <div className="size-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500/50">Fetching Candles</span>
           </div>
        </div>
      )}
    </div>
  );
}
